import type { Currency } from '@ps/web-core-enums';
import type { ClubRecentTournamentSummary } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import { Link, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import Arrow from '@swf/core/assets/icons/arrow.svg';
import { FormattedCurrencyAmount, stripHTML } from '@swf/poker';

import { PKR_LOADERS } from '../../../../constants';
import useSetLoadingOnUnmount from '../../../../hooks/useSetLoadingOnUnmount';
import { useHomeGamesWidget } from '../../../../model';
import { ClubRoute, GameTypes } from '../../../../types/common';
import { firstLetter } from '../../../../utils/firstLetter';
import { getAvatarColor } from '../../../../utils/getAvatarColor';
import useSeason from '../../Standings/hooks/useSeason';

import {
  TXTCLI_Prize,
  TXTCLI_Winner,
  TXTWCL_All_Results,
  TXTWCL_Last_Results
} from './LastTournamentResult.messages.json';
import styles from './LastTournamentResult.scss';
import LastTournamentSkeleton from './LastTournamentSkeleton';

const LastTournamentResult: React.FC = () => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const pathname = `/homegames/clubs/${clubId}/results/`;
  const { formatDate } = useIntl();
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const { noSeasons, seasonId } = useSeason();
  const { clubSeasonSummaries: clubSeasonSummariesLoading } = useSelector(widgetSelect.loading);

  const summaries = useSelector(widgetSelect.clubSummariesByDate);

  useSetLoadingOnUnmount(PKR_LOADERS.clubSeasonSummaries);

  useEffect(() => {
    widgetDispatch.fetchClubSeasons({
      clubId,
      latestOnly: true
    });
  }, [widgetDispatch, clubId]);

  useEffect(() => {
    widgetDispatch.fetchRecentTournamentSummaries({
      clubId: Number(clubId),
      seasonId: 0,
      limit: 1
    });
    return () => {
      widgetDispatch.clearRecentTournamentSummary();
    };
  }, [widgetDispatch, clubId, seasonId, noSeasons]);

  return !clubSeasonSummariesLoading ? (
    <>
      {Object.keys(summaries)
        .sort((a, b) => +new Date(b) - +new Date(a))
        .map((date) => {
          const group = summaries[date] as ClubRecentTournamentSummary[];

          return (
            <div className={styles.lastTournament} key={clubId}>
              <div className={styles.title}>
                <Translation {...TXTWCL_Last_Results} />
              </div>
              <div className={styles.section}>
                <div className={styles.date}>
                  <div>
                    {formatDate(date, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                  </div>
                  <div className={styles.linkContainer}>
                    <Link href={pathname}>
                      <span>
                        <Translation {...TXTWCL_All_Results} />
                      </span>
                      <Arrow />
                    </Link>
                  </div>
                </div>
                {group.map(
                  ({
                    tournId,
                    tournName,
                    prizePool,
                    gameType,
                    currency,
                    seasonId,
                    scalePM,
                    playMoney,
                    topPlayers
                  }) => (
                    <Link
                      alias="homeGamesClubs"
                      values={{
                        clubId,
                        section: ClubRoute.Results,
                        subsection: `${seasonId}-${tournId}`
                      }}
                      key={tournId}
                      className={styles.row}
                    >
                      <div className={styles.cell}>{stripHTML(tournName) || tournId}</div>
                      <div className={styles.cell}>
                        <span className={styles.label}>
                          {gameType && GameTypes[gameType] ? GameTypes[gameType] : null}
                        </span>
                      </div>
                      <div className={styles.cell}>
                        <div className={styles.winnerContainer}>
                          <div
                            className={styles.avatarIcon}
                            style={{
                              backgroundColor: getAvatarColor(topPlayers?.[0]?.userId)
                            }}
                          >
                            {topPlayers && topPlayers[0]?.place === 1 ? (
                              <span className={styles.userAvatar}>
                                {topPlayers[0].userId && firstLetter(topPlayers[0].userId)}
                              </span>
                            ) : null}
                          </div>
                          <div className={styles.winner}>
                            <span className={styles.label}>
                              <Translation {...TXTCLI_Winner} />
                            </span>
                            {topPlayers && topPlayers[0]?.place === 1 ? (
                              <span className={styles.username}>{topPlayers[0].userId}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className={classNames(styles.cell, styles.priceContainer)}>
                        <span className={styles.label}>
                          <Translation {...TXTCLI_Prize} />
                        </span>
                        <FormattedCurrencyAmount
                          amount={prizePool}
                          scalePM={scalePM}
                          isPlayMoney={playMoney}
                          currency={currency as Currency}
                        />
                      </div>
                      <div className={styles.cell}>
                        <Arrow />
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>
          );
        })}
    </>
  ) : !noSeasons ? (
    <LastTournamentSkeleton />
  ) : null;
};

export default LastTournamentResult;
